import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Send,
  Wrench,
  FileText,
  CreditCard,
  Building,
  Home,
  MapPin,
  CalendarDays,
  ChevronRight,
  ArrowLeft,
  Users,
  DollarSign,
  BarChart3,
  ClipboardCheck,
  UserCog,
  Zap,
  AlertTriangle,
  ClipboardList,
  MessageCircleWarning,
  ListTodo,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import NivaAvatar from './NivaAvatar';
import { Button } from './ui/button';
import ChatInterface from './ChatInterface';
import { tenantService } from '../services/tenantService';
import maintenanceService from '../services/maintenanceService';
import { activityService } from '../services/activityService';

// ── Tenant topic categories ────────────────────────────────────────
const TENANT_TOPIC_CATEGORIES = [
  {
    key: 'maintenance',
    label: 'Maintenance',
    icon: Wrench,
    color: 'from-orange-500 to-red-500',
    bgLight: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
    subOptions: [
      { label: 'Report an issue', prompt: 'I need to report a maintenance issue.' },
      { label: 'Check request status', prompt: 'Can you check the status of my maintenance requests?' },
      { label: 'Emergency repair', prompt: 'I have an emergency maintenance issue that needs immediate attention.' },
      { label: 'Mark issue as resolved', prompt: 'I want to mark a maintenance issue as resolved.' },
    ],
  },
  {
    key: 'lease',
    label: 'Lease Info',
    icon: FileText,
    color: 'from-blue-400 to-indigo-400',
    bgLight: 'bg-blue-50 border-blue-200',
    comingSoon: true,
    subOptions: [
      { label: 'View lease details' },
      { label: 'Renewal questions' },
      { label: 'Lease terms' },
    ],
  },
  {
    key: 'payments',
    label: 'Payments',
    icon: CreditCard,
    color: 'from-emerald-400 to-teal-400',
    bgLight: 'bg-emerald-50 border-emerald-200',
    comingSoon: true,
    subOptions: [
      { label: 'Payment history' },
      { label: 'Upcoming payments' },
      { label: 'Payment methods' },
    ],
  },
  {
    key: 'property',
    label: 'Property Info',
    icon: Building,
    color: 'from-violet-400 to-purple-400',
    bgLight: 'bg-violet-50 border-violet-200',
    comingSoon: true,
    subOptions: [
      { label: 'Property amenities' },
      { label: 'Contact management' },
      { label: 'Community rules' },
    ],
  },
  {
    key: 'my_account',
    label: 'My Account',
    icon: UserCog,
    color: 'from-slate-500 to-gray-600',
    bgLight: 'bg-slate-50 hover:bg-slate-100 border-slate-200',
    subOptions: [
      { label: 'Update my phone number', prompt: 'I want to update my phone number.' },
      { label: 'Update my name', prompt: 'I want to update my display name.' },
      { label: 'Turn off email notifications', prompt: 'Please turn off email notifications for me.' },
      { label: 'Turn off SMS notifications', prompt: 'Please turn off SMS notifications for me.' },
      { label: 'Set preferred contact method', prompt: 'I want to change my preferred contact method.' },
    ],
  },
];

// ── Landlord / Property Manager topic categories ───────────────────
const LANDLORD_TOPIC_CATEGORIES = [
  {
    key: 'maintenance',
    label: 'Maintenance',
    icon: Wrench,
    color: 'from-orange-500 to-red-500',
    bgLight: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
    subOptions: [
      { label: 'Open requests overview', prompt: 'Give me an overview of all open maintenance requests across my properties.' },
      { label: 'Urgent & emergency issues', prompt: 'Are there any urgent or emergency maintenance issues that need my attention?' },
      { label: 'Pending approvals', prompt: 'What maintenance requests are currently pending my approval?' },
      { label: 'Recently resolved', prompt: 'Which maintenance requests have been resolved recently?' },
    ],
  },
  {
    key: 'tenants',
    label: 'Tenants',
    icon: Users,
    color: 'from-blue-500 to-indigo-500',
    bgLight: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
    subOptions: [
      { label: 'Tenant overview', prompt: 'Give me a summary of my current tenants and their status.' },
      { label: 'Lease renewals', prompt: 'Which tenant leases are coming up for renewal soon?' },
      { label: 'Vacant units', prompt: 'Do I have any vacant units or upcoming vacancies?' },
      { label: 'Tenant communications', prompt: 'Help me draft a communication to send to my tenants.' },
      { label: 'Payment status', prompt: 'Check the payment status for one of my tenants.' },
    ],
  },
  {
    key: 'financials',
    label: 'Financials',
    icon: DollarSign,
    color: 'from-emerald-400 to-teal-400',
    bgLight: 'bg-emerald-50 border-emerald-200',
    comingSoon: true,
    subOptions: [
      { label: 'Maintenance cost summary' },
      { label: 'Pending & outstanding costs' },
      { label: 'NivaPay status' },
      { label: 'Cost by property' },
    ],
  },
  {
    key: 'portfolio',
    label: 'Portfolio',
    icon: BarChart3,
    color: 'from-violet-400 to-purple-400',
    bgLight: 'bg-violet-50 border-violet-200',
    comingSoon: true,
    subOptions: [
      { label: 'Portfolio overview' },
      { label: 'Property performance' },
      { label: 'Vendor & contractor info' },
      { label: 'Compliance & inspections' },
    ],
  },
  {
    key: 'account',
    label: 'Account & Plan',
    icon: Zap,
    color: 'from-indigo-500 to-violet-500',
    bgLight: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200',
    subOptions: [
      { label: 'Check my plan & usage', prompt: 'What is my current plan and how much of my usage have I used?' },
      { label: 'How many properties can I add?', prompt: 'How many properties can I still add on my current plan?' },
      { label: 'Niva messages remaining', prompt: 'How many Niva messages do I have left this month?' },
      { label: 'Upgrade my plan', prompt: 'I want to upgrade my plan. What options are available?' },
    ],
  },
];

// ── Landlord action items ──────────────────────────────────────────
const ACTION_CONFIGS = [
  {
    key: 'pendingApprovals',
    icon: ClipboardList,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
    dot: 'bg-amber-500',
    label: (n) => `${n} maintenance request${n !== 1 ? 's' : ''} pending your approval`,
    prompt: 'What maintenance requests are currently pending my approval?',
  },
  {
    key: 'emergencyCases',
    icon: AlertTriangle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200 hover:bg-red-100',
    dot: 'bg-red-500',
    label: (n) => `${n} emergency or urgent issue${n !== 1 ? 's' : ''} open right now`,
    prompt: 'Are there any urgent or emergency maintenance issues that need my attention?',
  },
  {
    key: 'escalations',
    icon: MessageCircleWarning,
    color: 'text-violet-600',
    bg: 'bg-violet-50 border-violet-200 hover:bg-violet-100',
    dot: 'bg-violet-500',
    label: (n) => `${n} tenant escalation${n !== 1 ? 's' : ''} need${n === 1 ? 's' : ''} follow-up`,
    prompt: 'Which tenants have recently escalated issues that need my follow-up?',
  },
  {
    key: 'openTasks',
    icon: ListTodo,
    color: 'text-sky-600',
    bg: 'bg-sky-50 border-sky-200 hover:bg-sky-100',
    dot: 'bg-sky-500',
    label: (n) => `${n} open task${n !== 1 ? 's' : ''} or follow-up${n !== 1 ? 's' : ''} pending`,
    prompt: 'What open tasks and follow-ups do I have pending?',
  },
];

function LandlordActionItems({ onItemClick }) {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      maintenanceService.getAll({ approvalStatus: 'pending' }),
      maintenanceService.getAll({ priority: 'Emergency', status: 'Open' }),
      activityService.getAll({ kind: 'email' }),
      activityService.getAll({ status: 'open' }),
    ])
      .then(([approvals, emergencies, emailActivities, openActivities]) => {
        if (cancelled) return;
        const approvalList = Array.isArray(approvals) ? approvals : [];
        const emergencyList = Array.isArray(emergencies) ? emergencies : [];
        const emailList = Array.isArray(emailActivities) ? emailActivities : [];
        const openList = Array.isArray(openActivities) ? openActivities : [];
        const escalationCount = emailList.filter((a) =>
          a.title?.toLowerCase().includes('escalation')
        ).length;
        const openTaskCount = openList.filter((a) => a.kind !== 'email').length;
        setCounts({
          pendingApprovals: approvalList.length,
          emergencyCases: emergencyList.length,
          escalations: escalationCount,
          openTasks: openTaskCount,
        });
      })
      .catch(() => { if (!cancelled) setCounts(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Checking your portfolio…</span>
      </div>
    );
  }

  const activeItems = counts
    ? ACTION_CONFIGS.filter((cfg) => (counts[cfg.key] ?? 0) > 0)
    : [];

  if (activeItems.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
        <span>You're all caught up, no urgent items right now.</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
        Needs your attention
      </p>
      <div className="flex flex-col gap-2">
        {activeItems.map((cfg) => {
          const Icon = cfg.icon;
          const n = counts[cfg.key];
          return (
            <button
              key={cfg.key}
              onClick={() => onItemClick(cfg.prompt)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${cfg.bg}`}
            >
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
              <Icon className={`h-4 w-4 flex-shrink-0 ${cfg.color}`} />
              <span className={`text-sm font-medium flex-1 ${cfg.color}`}>
                {cfg.label(n)}
              </span>
              <ChevronRight className={`h-4 w-4 flex-shrink-0 ${cfg.color} opacity-60`} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

const NivaChat = ({ user }) => {
  const location = useLocation();
  const [tenantProfile, setTenantProfile] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [chatStarted, setChatStarted] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const inputRef = useRef(null);

  const isTenant = user?.userType === 'Tenant';
  const topicCategories = isTenant ? TENANT_TOPIC_CATEGORIES : LANDLORD_TOPIC_CATEGORIES;

  // Auto-start chat if navigated here with a pre-filled prompt (e.g. from dashboard CTAs)
  useEffect(() => {
    const prompt = location.state?.nivaPrompt;
    if (prompt) {
      setInitialMessage(prompt);
      setChatStarted(true);
      // Clear state so Back button doesn't re-trigger
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isTenant) {
      tenantService.getMe()
        .then((data) => setTenantProfile(data))
        .catch((err) => console.error('Failed to load tenant profile:', err))
        .finally(() => setLoadingProfile(false));
    } else {
      setLoadingProfile(false);
    }
  }, [isTenant]);

  // Derive a short first name safe for display — handles emails used as usernames
  const rawName = (user?.name?.trim() || user?.username || '');
  const rawFirst = rawName.includes('@')
    ? rawName.split('+')[0].split('@')[0]   // "yogesh" from "yogesh+test1@..."
    : rawName.split(' ')[0];
  const firstName = rawFirst
    ? (rawFirst.length > 14 ? rawFirst.slice(0, 14) + '…' : rawFirst)
    : 'there';

  const property = tenantProfile?.property;

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

  const handleSendFromInput = () => {
    const text = inputValue.trim();
    if (!text) return;
    setInitialMessage(text);
    setChatStarted(true);
  };

  const handleSubOptionClick = (prompt) => {
    setInitialMessage(prompt);
    setChatStarted(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendFromInput();
    }
  };

  const handleCategoryClick = (key) => {
    setActiveCategory(activeCategory === key ? null : key);
  };

  const handleBackToWelcome = () => {
    setChatStarted(false);
    setInitialMessage('');
    setInputValue('');
    setActiveCategory(null);
  };

  // ── Chat view ──────────────────────────────────────────────────
  if (chatStarted) {
    return (
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Back button bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-white/80 backdrop-blur-sm flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToWelcome}
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            New conversation
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <NivaAvatar size="sm" />
            <span className="text-sm font-semibold text-gray-700">Niva</span>
          </div>
        </div>

        {/* Chat */}
        <div className="flex-1 px-4 py-3 overflow-hidden">
          <ChatInterface
            user={user}
            embedded={true}
            mode="general"
            initialMessage={initialMessage}
          />
        </div>
      </div>
    );
  }

  // ── Welcome view ───────────────────────────────────────────────
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] overflow-y-auto">
      {/* Property summary bar */}
      {isTenant && tenantProfile && property && (
        <div className="flex-shrink-0 bg-gradient-to-r from-slate-50 to-blue-50/50 border-b px-4 py-2.5">
          <div className="max-w-3xl mx-auto grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-x-4 gap-y-1.5">
            <div className="flex items-center gap-1.5 col-span-2 sm:col-auto">
              <Home className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              <span className="font-semibold text-xs truncate">{property.name || 'My Property'}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground min-w-0">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="text-xs truncate">{property.address || 'Address on file'}</span>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Unit <span className="font-medium text-foreground">{tenantProfile.unitNumber}</span>
            </span>
            <div className="flex items-center gap-1 text-xs text-muted-foreground col-span-2 sm:col-auto">
              <CalendarDays className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{formatDate(tenantProfile.leaseStart)} to {formatDate(tenantProfile.leaseEnd)}</span>
            </div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium w-fit ${
              tenantProfile.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {tenantProfile.status}
            </span>
          </div>
        </div>
      )}

      {/* Center content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:py-8">
        <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
          {/* Niva avatar */}
          <div className="flex justify-center">
            <NivaAvatar size="lg" className="shadow-lg shadow-cyan-500/20" />
          </div>

          {/* Welcome text */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-gray-900 break-words">
              Welcome, {firstName}!
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
              I'm <span className="font-semibold text-cyan-600">Niva</span>, your property assistant.
              {isTenant
                ? ' Ask me anything about your home, lease, or maintenance needs.'
                : ' Ask me about your properties, tenants, maintenance pipeline, or financials.'}
            </p>
          </div>

          {/* Landlord action items */}
          {!isTenant && (
            <LandlordActionItems onItemClick={handleSubOptionClick} />
          )}

          {/* Input area */}
          <div className="relative">
            <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-2xl px-4 py-3 shadow-sm focus-within:border-cyan-400 focus-within:shadow-md focus-within:shadow-cyan-500/10 transition-all">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask Niva anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent outline-none text-base text-gray-800 placeholder:text-gray-400"
              />
              <Button
                onClick={handleSendFromInput}
                disabled={!inputValue.trim()}
                size="icon"
                className="rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 h-9 w-9 flex-shrink-0 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Topic buttons */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              {topicCategories.map((cat) => {
                const Icon = cat.icon;
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => handleCategoryClick(cat.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all text-sm w-full sm:w-auto ${
                      isActive
                        ? `${cat.bgLight} border-2 scale-[1.02]`
                        : cat.comingSoon
                          ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`h-6 w-6 rounded-md bg-gradient-to-br ${cat.color} flex items-center justify-center flex-shrink-0 ${cat.comingSoon ? 'opacity-60' : ''}`}>
                      <Icon className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className={`font-medium leading-none ${cat.comingSoon ? 'text-gray-400' : 'text-gray-700'}`}>{cat.label}</span>
                    {cat.comingSoon && (
                      <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide leading-none ml-auto">Soon</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Sub-options (expanded) */}
            {activeCategory && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                  {(() => {
                    const activeCat = topicCategories.find((c) => c.key === activeCategory);
                    return (
                      <>
                        {activeCat?.comingSoon && (
                          <div className="flex justify-end mb-2 px-1">
                            <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wide">
                              Coming soon
                            </span>
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          {activeCat?.subOptions.map((opt, idx) =>
                            activeCat.comingSoon ? (
                              <div
                                key={idx}
                                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-gray-400 cursor-not-allowed select-none"
                              >
                                <span>{opt.label}</span>
                                <ChevronRight className="h-4 w-4 text-gray-300" />
                              </div>
                            ) : (
                              <button
                                key={idx}
                                onClick={() => handleSubOptionClick(opt.prompt)}
                                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors group text-left"
                              >
                                <span>{opt.label}</span>
                                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                              </button>
                            )
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NivaChat;
